/**
 * useFlowCompletion — the wizard's source of truth for "what's missing" and progress.
 *
 * Backed by GET /internal/flow-instances/:id/completion (engine in apps/api). Every mutation
 * that affects an engine input (field saves, goal edits, classification/sumsub/trapets/
 * financial/product-assessment writes, custom-product CRUD) should invalidate the same key
 * so the progress bar refreshes — use `useInvalidateFlowCompletion` for that.
 */

import { toFlowCompletionSummary } from "@repo/shared";
import type {
  FlowRequirements,
  FollowupConflictAccount,
  MissingRequirement,
  MissingRequirementReason,
} from "@repo/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import * as flowInstancesApi from "@/lib/api/flow-instances";

export const flowCompletionQueryKey = (instanceId: string | null) =>
  ["flowInstances", instanceId, "completion"] as const;

/**
 * Derived Sets used by error highlighting (Follow-up A — H1/H2/H3/H4) to decide which
 * tabs, widgets, and fields are missing without scanning `missing[]` linearly.
 * Empty sets when `completion` is null so callers can `.has(...)` unconditionally.
 */
export type CompletionMarkers = {
  /** pageId → has at least one missing item. Drives outer page tabs + global page tabs. */
  missingPageIds: Set<string>;
  /** goalId → has at least one missing item (goal-page widget OR any of its portfolios). Drives multi-goal selector tabs. */
  missingGoalIds: Set<string>;
  /**
   * portfolioId → has at least one missing item (QP-150). Drives the portfolio tab-strip
   * error dots on the portfolio page. The portfolio page renders once per portfolio sharing
   * one page definition, so attribution is by portfolioId, not pageId.
   */
  missingPortfolioIds: Set<string>;
  /** `"goalId:pageId"` → drives multi-goal sub-page nav buttons. */
  missingGoalPageKeys: Set<string>;
  /** `"portfolioId:pageId"` → drives the portfolio page's per-portfolio sub-nav (QP-150). */
  missingPortfolioPageKeys: Set<string>;
  /**
   * componentId → drives per-widget red borders (H2) for **instance-scoped** widgets
   * (no `goalId` on the missing entry — `client_classification`, `sumsub_verification`,
   * `trapets_lookup`, `product_assessment`, `financial_situation`). Goal-scoped widgets
   * MUST use `missingGoalComponentKeys` instead, otherwise a single goal's gap would
   * paint every goal's instance of the same component definition red (see
   * `missingGoalComponentKeys` below).
   */
  missingComponentIds: Set<string>;
  /**
   * `"goalId:componentId"` → drives per-widget red borders (H2) for **goal-scoped**
   * widgets (`risk_assessment`, `custom_portfolio`, `portfolio_display`). The engine
   * fans out per goal during traversal and emits a separate missing entry with the
   * owning `goalId`, but the underlying component definition is shared across all
   * goals on the same goal page, so the raw `componentId` alone is ambiguous. Covers
   * both `kind: "component"` and `kind: "snapshot"` when the entry carries a `goalId`.
   */
  missingGoalComponentKeys: Set<string>;
  /**
   * `"portfolioId:componentId"` → drives per-widget red borders for **portfolio-scoped**
   * widgets (QP-150): `portfolio_input`, `custom_portfolio`, and the per-portfolio snapshot
   * display components. These render once per portfolio sharing a single component
   * definition, so keying by `goalId:componentId` alone would paint every portfolio of the
   * goal red on one portfolio's gap — the same failure `missingGoalComponentKeys` prevents
   * across goals, one level down. MUST be used instead of `missingGoalComponentKeys` for
   * any component on the portfolio page.
   */
  missingPortfolioComponentKeys: Set<string>;
  /** `"fieldId"` (global), `"goalId:fieldId"` (goal-scoped), or `"portfolioId:fieldId"`
   * (portfolio-scoped) → drives per-field borders (H3). */
  missingFieldKeys: Set<string>;
  /**
   * goalId → at least one goal-scoped `kind: "snapshot"` entry exists on the goal. Drives a
   * per-goal regenerate banner. (Post-QP-150 the snapshot display components are all
   * portfolio-scoped, so this stays empty in practice — kept for any future goal-scoped
   * snapshot; `missingSnapshotPortfolioIds` is the live one.)
   */
  missingSnapshotGoalIds: Set<string>;
  /**
   * portfolioId → at least one `kind: "snapshot"` entry exists on the portfolio (QP-150).
   * Drives the per-portfolio regenerate banner.
   */
  missingSnapshotPortfolioIds: Set<string>;
  /**
   * componentId → at least one INSTANCE-SCOPED `kind: "snapshot"` entry exists
   * (no `goalId`). Drives the regenerate banner alongside `missingSnapshotGoalIds`
   * per D36 (Phase 2.5). Covers `cost_table` / `cost_chart` placed on global pages
   * (consuming `instance_cost`); will cover future instance-scoped snapshot types
   * when they land.
   */
  missingInstanceSnapshotComponents: Set<string>;
  /** `"layoutId:tabId"` → drives inner `tabs_block` tab markers (H4). Empty until H4 backend extension lands. */
  missingTabKeys: Set<string>;
  /**
   * QL-43 — `"pageId"` (global page) or `"goalId:pageId"` (goal page) → the page's
   * required advisor note is still empty. Drives the red border on the notes editor.
   * Keyed by page rather than component because the editor is page chrome, not a
   * placed component, so it has no componentId to key by.
   */
  missingAdvisorNoteKeys: Set<string>;
  /**
   * QL-138 — component key → the engine's machine-readable `reason` for that gap,
   * so a widget can render the specific message instead of a generic one. Keyed
   * exactly like the `missing*ComponentKeys` sets above (same precedence:
   * `portfolioId:componentId` → `goalId:componentId` → bare `componentId`), so a
   * widget looks its reason up with the same key it already uses for the border.
   * Only entries that carry a reason are present.
   */
  missingComponentReasons: Map<string, MissingRequirementReason>;
  /**
   * QL-314 — goal IDs that have an advisor-review blocking item. The public
   * flow renders an alert banner on these goals — the client can't resolve it,
   * only the advisor can. Populated from `kind: "advisor_review"` entries.
   */
  missingAdvisorReviewGoalIds: Set<string>;
  /**
   * QL-314 / QL-313 — reasons keyed by goalId for advisor-review items, in
   * engine order and deduplicated. A goal can be blocked by more than one
   * compliance component at once (volatility AND equity share), and every
   * reason must reach the client — a single-reason map silently dropped all
   * but the last one.
   */
  advisorReviewReasons: Map<string, MissingRequirementReason[]>;
  /**
   * QL-493 — reasons of `kind: "config_error"` entries, deduplicated, in
   * engine order: the flow TEMPLATE cannot enforce a risk bound its risk model
   * sets. Flow-level, no anchor, so nothing can be highlighted; the advisor
   * wizard renders an always-visible template alert and the public flow adds
   * a generic line to its advisor-review alert.
   */
  configErrorReasons: MissingRequirementReason[];
  /**
   * QL-576 — `kind: "followup_conflict"` entries, in engine order: a
   * follow-up whose relinked accounts would share a wrapper type on one
   * successor goal (bank data drifted after creation). Flow-level, no
   * anchor. The advisor wizard renders an always-visible alert naming the
   * accounts; the public flow only joins `missingAdvisorReviewGoalIds` /
   * `advisorReviewReasons` (the generic "advisor action required" line —
   * the client must not learn bank-account details).
   */
  followupConflicts: Array<{
    goalId: string;
    reason: MissingRequirementReason;
    wrapperType: string | null;
    accounts: FollowupConflictAccount[];
  }>;
};

/**
 * Pure derivation of {@link CompletionMarkers} from an engine `missing[]` array.
 * Shared by the advisor `useFlowCompletion` and the public `usePublicFlowCompletion`
 * so the D27/D28/D35/D36/D38 marker rules stay identical across both surfaces.
 */
export function deriveMarkers(missing: MissingRequirement[]): CompletionMarkers {
  const missingPageIds = new Set<string>();
  const missingGoalIds = new Set<string>();
  const missingPortfolioIds = new Set<string>();
  const missingGoalPageKeys = new Set<string>();
  const missingPortfolioPageKeys = new Set<string>();
  const missingComponentIds = new Set<string>();
  const missingAdvisorReviewGoalIds = new Set<string>();
  const advisorReviewReasons = new Map<string, MissingRequirementReason[]>();
  const missingGoalComponentKeys = new Set<string>();
  const missingPortfolioComponentKeys = new Set<string>();
  const missingFieldKeys = new Set<string>();
  const missingSnapshotGoalIds = new Set<string>();
  const missingSnapshotPortfolioIds = new Set<string>();
  const missingInstanceSnapshotComponents = new Set<string>();
  const missingTabKeys = new Set<string>();
  const missingAdvisorNoteKeys = new Set<string>();
  const missingComponentReasons = new Map<string, MissingRequirementReason>();
  const configErrorReasons: MissingRequirementReason[] = [];
  const followupConflicts: CompletionMarkers["followupConflicts"] = [];
  for (const m of missing) {
    // D38 `kind: "instance_snapshot"` entries have no page anchor — skip the
    // page/goal/component derivations entirely. The wizard reads these via an
    // inline `completion.missing.some(...)` check, not via these Sets.
    if (m.kind === "instance_snapshot") continue;
    // QL-493 `kind: "config_error"` entries are flow-level template gaps with
    // no anchor either; collected as reasons for the text alerts.
    if (m.kind === "config_error") {
      if (m.reason && !configErrorReasons.includes(m.reason)) configErrorReasons.push(m.reason);
      continue;
    }
    // QL-576 `kind: "followup_conflict"` — goal-level bank-data conflict. The
    // advisor wizard renders it as an always-visible alert (followupConflicts);
    // the public flow shows the generic "advisor action required" line, so it
    // also joins the advisor-review sets. Goal dot on both.
    if (m.kind === "followup_conflict") {
      if (m.goalId) {
        missingGoalIds.add(m.goalId);
        missingAdvisorReviewGoalIds.add(m.goalId);
        if (m.reason) {
          const reasons = advisorReviewReasons.get(m.goalId) ?? [];
          if (!reasons.includes(m.reason)) reasons.push(m.reason);
          advisorReviewReasons.set(m.goalId, reasons);
        }
      }
      followupConflicts.push({
        goalId: m.goalId ?? "",
        reason: m.reason ?? "followup_account_wrapper_conflict",
        wrapperType: m.wrapperType ?? null,
        accounts: m.accounts ?? [],
      });
      continue;
    }
    // QL-314 `kind: "advisor_review"` entries mark goals blocked by advisor-only
    // components placed on INTERNAL pages. They carry goalId (+ reason) and no
    // pageId / componentId — there is no public page to anchor them to. Add to
    // the advisor-review sets and the goal dot, then skip component-level
    // derivations. The public UI renders these as a per-goal banner (goal
    // pages) and a flow-level alert beside Submit (every flow shape).
    if (m.kind === "advisor_review") {
      if (m.goalId) {
        missingGoalIds.add(m.goalId);
        missingAdvisorReviewGoalIds.add(m.goalId);
        if (m.reason) {
          const reasons = advisorReviewReasons.get(m.goalId) ?? [];
          if (!reasons.includes(m.reason)) reasons.push(m.reason);
          advisorReviewReasons.set(m.goalId, reasons);
        }
      }
      continue;
    }
    if (m.pageId) missingPageIds.add(m.pageId);
    // A portfolio-scoped entry carries BOTH goalId and portfolioId (QP-150): the goal tab
    // still dots (one of its portfolios has a gap), and the portfolio tab strip dots too.
    if (m.goalId) {
      missingGoalIds.add(m.goalId);
      missingGoalPageKeys.add(`${m.goalId}:${m.pageId}`);
    }
    if (m.portfolioId) {
      missingPortfolioIds.add(m.portfolioId);
      missingPortfolioPageKeys.add(`${m.portfolioId}:${m.pageId}`);
    }
    // Widget red borders (H2). Keying precedence: portfolio-scoped widgets key by
    // `portfolioId:componentId`, goal-scoped by `goalId:componentId`, instance-scoped by
    // bare componentId — each level avoids painting siblings that share the definition.
    if (m.componentId) {
      const componentKey = m.portfolioId
        ? `${m.portfolioId}:${m.componentId}`
        : m.goalId
          ? `${m.goalId}:${m.componentId}`
          : m.componentId;
      if (m.portfolioId) {
        missingPortfolioComponentKeys.add(componentKey);
      } else if (m.goalId) {
        missingGoalComponentKeys.add(componentKey);
      } else {
        missingComponentIds.add(componentKey);
      }
      // QL-138 — the specific failure reason, under the same key as the border marker.
      if (m.reason) missingComponentReasons.set(componentKey, m.reason);
    }
    if (m.kind === "field" && m.fieldId) {
      if (m.portfolioId) {
        missingFieldKeys.add(`${m.portfolioId}:${m.fieldId}`);
      } else if (m.goalId) {
        missingFieldKeys.add(`${m.goalId}:${m.fieldId}`);
      } else {
        missingFieldKeys.add(m.fieldId);
      }
    }
    // Regenerate banner: portfolio-scoped snapshots key by portfolioId (QP-150), the live
    // path post-migration; goal-scoped → goalId (kept for any future goal snapshot);
    // instance-scoped (no goal/portfolio) → componentId.
    if (m.kind === "snapshot") {
      if (m.portfolioId) missingSnapshotPortfolioIds.add(m.portfolioId);
      else if (m.goalId) missingSnapshotGoalIds.add(m.goalId);
      else if (m.componentId) missingInstanceSnapshotComponents.add(m.componentId);
    }
    // QL-43 — page-level advisor note. No componentId, so it keys by page (+ goal on a
    // goal page, where the editor renders once per goal).
    if (m.kind === "advisor_note" && m.pageId) {
      missingAdvisorNoteKeys.add(m.goalId ? `${m.goalId}:${m.pageId}` : m.pageId);
    }
    // H4 (when backend lands): `layoutId`/`tabId` optional fields on MissingRequirement.
    const layoutId = (m as { layoutId?: string }).layoutId;
    const tabId = (m as { tabId?: string }).tabId;
    if (layoutId && tabId) missingTabKeys.add(`${layoutId}:${tabId}`);
  }
  return {
    missingPageIds,
    missingGoalIds,
    missingPortfolioIds,
    missingGoalPageKeys,
    missingPortfolioPageKeys,
    missingComponentIds,
    missingGoalComponentKeys,
    missingPortfolioComponentKeys,
    missingFieldKeys,
    missingSnapshotGoalIds,
    missingSnapshotPortfolioIds,
    missingInstanceSnapshotComponents,
    missingTabKeys,
    missingAdvisorNoteKeys,
    missingComponentReasons,
    missingAdvisorReviewGoalIds,
    advisorReviewReasons,
    configErrorReasons,
    followupConflicts,
  };
}

export function useFlowCompletion(instanceId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: flowCompletionQueryKey(instanceId),
    queryFn: () => flowInstancesApi.getFlowCompletion(instanceId!),
    enabled: !!instanceId,
    // Always considered stale — every mutation invalidates this key, the query refetches.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const markers = useMemo<CompletionMarkers>(() => deriveMarkers(data?.missing ?? []), [data]);

  return { completion: data ?? null, markers, error, isLoading };
}

/**
 * Invalidate the completion query for an instance — call from `onSuccess` of any mutation
 * that writes engine-input state (field values, goal rows, calculatedData blobs, custom
 * products, client.type). Phase 1 slices wire this into the relevant hooks.
 */
export function useInvalidateFlowCompletion() {
  const qc = useQueryClient();
  return useCallback(
    (instanceId: string) => qc.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) }),
    [qc],
  );
}

/**
 * Imperatively set the completion cache — used by the wizard's submit handler when it
 * receives a 422 with the latest FlowRequirements, so the progress bar reflects server
 * truth immediately without an extra round trip. Stores the lean summary the query
 * itself serves, so the 422 body's per-item `steps` never lands in the cache (QL-676).
 */
export function setFlowCompletionCache(
  qc: ReturnType<typeof useQueryClient>,
  instanceId: string,
  requirements: FlowRequirements,
) {
  qc.setQueryData(flowCompletionQueryKey(instanceId), toFlowCompletionSummary(requirements));
}
